/**
 * The screen-print JOB PACKET — the run detail grown into everything the screen room needs
 * (docs/screen-print-station-audit-and-plan-v1.md § 4.2, phase P3). Rendered by the office page
 * (`/screen-print`) today and by the kiosk (P3b) next, so it is built once here and takes the
 * surface-specific buttons as slots.
 *
 * DISPLAY ONLY. Nothing here presses a garment, draws a blank or touches `pressedAt` — pressing stays
 * on `/press`, the reviewed money surface (plan § 7). The packet renders no tick and no "pressed"
 * button, and the page test pins that.
 *
 * Sources, section by section (nothing is drawn that has no data behind it):
 *  - header counts: `BatchSummary` + `runSetups` + the joined specs. F1: "N screens" only once EVERY
 *    design on the run has a spec; "colours not set" otherwise — never a number that is wrong 3× on
 *    multi-colour art.
 *  - the mockup: the Shopify product image via `GET /catalog/:storeId/products/:productId` once per
 *    distinct `productId` (frozen on the work item at build, P1), and the art laid on the S&S colourway
 *    photo that `useBlankNames()` already carries.
 *  - the press carousel: `GET /presses` + `screens[].head / hex / meshCount`.
 *  - the screens: the design's `screenPrintSpec`; the master via `DesignFileView.downloadUrl`, which
 *    the server names `<styleCode ?? name>-<band>-<W>x<H>in.png` (band-correct).
 *  - ink & cure, pallet & placement: the spec, the alignment box, the seed placement rules.
 *  - blanks to pull and press progress: as before, restyled.
 */
import type { BlankOnHand } from '@fsp/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Download, FileText, ImageOff, Layers, TriangleAlert } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { fetchAllDesigns } from '@/lib/api/designs';
import { useApiClient } from '@/lib/api/hooks';
import { cn } from '@/lib/utils';
import { BATCH_STATUS_META, type BatchSummary, batchLabel, blanksToPull } from './batch-lib';
import type { DesignSummary } from './design-lib';
import { JobTicket } from './job-ticket';
import { relativeAge } from './orders-queue';
import { PressCarousel } from './press-carousel';
import {
  DEFAULT_PLATEN_ID,
  fmtIn,
  isWholeFileAlignment,
  type Platen,
  platenById,
} from './press-guide-lib';
import { usePresses } from './presses-page';
import { stampPull } from './pull-stamp';
import { designLabel, runGarmentCount, runSetups } from './screen-print-lib';
import {
  artOverlay,
  cureLines,
  distinctProducts,
  firstBlankSku,
  joinSpecs,
  palletPlacement,
  placementCaption,
  printInchesLabel,
  printSizeOf,
  productKey,
  resolveRunPress,
  ringScreensForRun,
  runScreenCount,
  type SetupWithSpec,
  setupsMissingSpec,
} from './screen-print-packet-lib';
import { pressShapeLabel, type ScreenSpec, stationLayout } from './screen-print-spec-lib';

// ── Data ──────────────────────────────────────────────────────────────────────────────────────────

/**
 * Every design, by id. The SAME query the library uses (`['designs']`, `fetchAllDesigns`) — one cache
 * entry, one fetch, and the `designs` change domain invalidates both at once, so a spec typed in the
 * library shows up here without a refresh. One request for every run on the page rather than one per
 * design: the library is small at this scale and the page needs the whole set for its Screens column.
 */
export function useDesignsById(): {
  byId: Map<string, DesignSummary>;
  isLoading: boolean;
  isError: boolean;
} {
  const client = useApiClient();
  const query = useQuery({
    queryKey: ['designs'],
    queryFn: () => fetchAllDesigns(client),
  });
  const byId = useMemo(
    () => new Map((query.data ?? []).map((d) => [d.designId, d] as const)),
    [query.data],
  );
  return { byId, isLoading: query.isLoading, isError: query.isError };
}

/**
 * One product image per distinct product on the run. Keyed under `catalog-detail` so the catalog
 * change domain refreshes it. A product that was deleted or never synced is NOT an error for the
 * packet — its card says so and everything else renders.
 */
export function useProductImages(products: readonly { storeId: string; productId: string }[]): {
  images: Record<string, string | null>;
  isLoading: boolean;
} {
  const client = useApiClient();
  const keys = products.map((p) => productKey(p.storeId, p.productId));
  const query = useQuery({
    queryKey: ['catalog-detail', 'run-products', ...keys],
    enabled: products.length > 0,
    queryFn: async () => {
      const entries = await Promise.all(
        products.map(async (p) => {
          const key = productKey(p.storeId, p.productId);
          const { data, error } = await client.GET('/catalog/{storeId}/products/{productId}', {
            params: { path: { storeId: p.storeId, productId: p.productId } },
          });
          if (error || !data?.ok) return [key, null] as const;
          return [key, data.data.product.featuredImageUrl ?? null] as const;
        }),
      );
      return Object.fromEntries(entries) as Record<string, string | null>;
    },
  });
  return { images: query.data ?? {}, isLoading: query.isLoading };
}

// ── Small pieces ──────────────────────────────────────────────────────────────────────────────────

function Section({
  title,
  hint,
  children,
  className,
}: {
  title: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('space-y-1.5', className)}>
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </h3>
        {hint && <span className="text-right text-xs text-muted-foreground">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

function Chip({ children, title }: { children: ReactNode; title?: string }) {
  return (
    <span
      title={title}
      className="inline-flex items-center gap-1 rounded-md border bg-muted/40 px-1.5 py-0.5 text-xs"
    >
      {children}
    </span>
  );
}

/** The ink's colour, or an empty ring when the spec has no hex. */
function Swatch({ hex }: { hex: string | undefined }) {
  return (
    <span
      aria-hidden="true"
      className="inline-block size-3 rounded-sm border border-black/20"
      style={{ background: hex ?? 'transparent' }}
    />
  );
}

const BAND_LABEL = { adult: 'Adult', youth: 'Youth' } as const;

/** A plain tee, for the art-on-blank card when the blank has no S&S photo (or never resolved). */
function TeeSilhouette({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 120 140" aria-hidden="true" className={className}>
      <path
        d="M30 18 L48 8 Q60 16 72 8 L90 18 L108 34 L96 46 L88 40 L88 128 L32 128 L32 40 L24 46 L12 34 Z"
        fill="#262626"
        stroke="#404040"
      />
    </svg>
  );
}

// ── The mockup ────────────────────────────────────────────────────────────────────────────────────

function PhotoCard({ caption, children }: { caption: ReactNode; children: ReactNode }) {
  return (
    <figure className="min-w-0 space-y-1">
      <div className="relative aspect-[6/7] overflow-hidden rounded-md border bg-neutral-100 dark:bg-neutral-900">
        {children}
      </div>
      <figcaption className="truncate text-[11px] text-muted-foreground">{caption}</figcaption>
    </figure>
  );
}

function ProductPhoto({
  title,
  url,
  loading,
}: {
  title: string;
  url: string | null | undefined;
  loading: boolean;
}) {
  return (
    <PhotoCard caption={<>Shopify product image · {title}</>}>
      {url ? (
        <img src={url} alt={title} className="absolute inset-0 size-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 p-3 text-center text-[11px] text-muted-foreground">
          <ImageOff className="size-5" aria-hidden="true" />
          {loading ? 'Loading…' : 'No product image in the catalog'}
        </div>
      )}
    </PhotoCard>
  );
}

/**
 * The design preview laid over the S&S colourway photo at roughly true proportion (§ 3.4). Approximate
 * by construction and captioned so — it answers "black tee, glow back, this big" at a glance and
 * nothing downstream reads it.
 */
function ArtOnBlank({ setup, blank }: { setup: SetupWithSpec; blank: BlankOnHand | undefined }) {
  const file = setup.sizeBand === 'youth' ? setup.design?.youth : setup.design?.adult;
  const size = printSizeOf(setup.design, setup.sizeBand);
  const overlay = artOverlay(setup.location, setup.sizeBand, size);
  const blankName = blank
    ? [blank.brandName, blank.styleName, blank.colorName].filter(Boolean).join(' ')
    : undefined;
  const inches = printInchesLabel(setup.design, setup.sizeBand);
  return (
    <PhotoCard
      caption={
        <>
          <span className="capitalize">{setup.location}</span>
          {inches ? ` · ${inches}` : ''} ·{' '}
          {blankName ? `on ${blankName}` : 'blank photo unavailable'}
        </>
      }
    >
      {blank?.imageUrl ? (
        <img src={blank.imageUrl} alt="" className="absolute inset-0 size-full object-cover" />
      ) : (
        <TeeSilhouette className="absolute inset-0 size-full" />
      )}
      {file?.previewUrl ? (
        <img
          src={file.previewUrl}
          alt={`${designLabel(setup)} art`}
          className="absolute"
          style={{
            left: `${overlay.leftPct}%`,
            top: `${overlay.topPct}%`,
            width: `${overlay.widthPct}%`,
          }}
        />
      ) : (
        <div className="absolute inset-x-3 bottom-3 rounded bg-black/60 px-2 py-1 text-center text-[11px] text-white">
          art preview not ready
        </div>
      )}
    </PhotoCard>
  );
}

// ── Pallet & placement ────────────────────────────────────────────────────────────────────────────

/**
 * The pallet at true proportion with the art placed by the seed rule and the alignment box dashed
 * inside it (what the press guides line up with). Inches are the SVG unit, so nothing here is scaled by
 * hand; the collar edge is the top.
 */
function PalletSketch({ setup, platen }: { setup: SetupWithSpec; platen: Platen }) {
  const size = printSizeOf(setup.design, setup.sizeBand);
  const placed = palletPlacement(setup.location, setup.sizeBand, size, platen);
  const { box } = placed;
  const file = setup.sizeBand === 'youth' ? setup.design?.youth : setup.design?.adult;
  const align = setup.design?.alignment?.[setup.sizeBand];
  const alignBox =
    align && !isWholeFileAlignment(align)
      ? {
          x: box.x + align.x * box.w,
          y: box.y + align.y * box.h,
          w: align.w * box.w,
          h: align.h * box.h,
        }
      : null;
  const pad = 0.6;
  return (
    <svg
      viewBox={`${-pad} ${-1.6} ${platen.acrossIn + pad * 2} ${platen.deepIn + 1.6 + pad}`}
      className="w-full max-w-[150px]"
      role="img"
      aria-label={`${setup.location} art on a ${platen.acrossIn} × ${platen.deepIn} pallet`}
      data-testid="pallet-sketch"
    >
      <text
        x={platen.acrossIn / 2}
        y={-0.55}
        textAnchor="middle"
        fontSize={0.85}
        fontWeight={700}
        fill="currentColor"
        opacity={0.6}
        style={{ letterSpacing: '.1em' }}
      >
        COLLAR
      </text>
      <rect
        x={0}
        y={0}
        width={platen.acrossIn}
        height={platen.deepIn}
        rx={0.4}
        fill="currentColor"
        opacity={0.06}
      />
      <rect
        x={0}
        y={0}
        width={platen.acrossIn}
        height={platen.deepIn}
        rx={0.4}
        fill="none"
        stroke="currentColor"
        strokeWidth={0.12}
        opacity={0.6}
      />
      <rect
        x={box.x}
        y={box.y}
        width={box.w}
        height={box.h}
        fill="#fff"
        fillOpacity={0.5}
        stroke="currentColor"
        strokeWidth={0.1}
        strokeDasharray={placed.guessed ? '0.4 0.3' : undefined}
      />
      {file?.previewUrl && (
        <image
          href={file.previewUrl}
          x={box.x}
          y={box.y}
          width={box.w}
          height={box.h}
          preserveAspectRatio="xMidYMid meet"
        />
      )}
      {alignBox && (
        <rect
          data-testid="alignment-box"
          x={alignBox.x}
          y={alignBox.y}
          width={alignBox.w}
          height={alignBox.h}
          fill="none"
          stroke="#10b981"
          strokeWidth={0.14}
          strokeDasharray="0.35 0.25"
        />
      )}
      <text
        x={box.x + box.w / 2}
        y={box.y + box.h + 0.85}
        textAnchor="middle"
        fontSize={0.7}
        fill="currentColor"
        opacity={0.7}
      >
        {size ? `${fmtIn(size.widthIn)} × ${fmtIn(size.heightIn)}` : 'size unknown'}
      </text>
    </svg>
  );
}

function PalletRow({ setup, platen }: { setup: SetupWithSpec; platen: Platen }) {
  const size = printSizeOf(setup.design, setup.sizeBand);
  const placed = palletPlacement(setup.location, setup.sizeBand, size, platen);
  const align = setup.design?.alignment?.[setup.sizeBand];
  const aligned = Boolean(align && !isWholeFileAlignment(align));
  return (
    <div className="grid grid-cols-[150px_1fr] items-start gap-3">
      <PalletSketch setup={setup} platen={platen} />
      <dl className="grid grid-cols-[84px_1fr] gap-x-2 gap-y-1 text-sm">
        <dt className="text-muted-foreground">Setup</dt>
        <dd>
          <span className="font-medium capitalize">{setup.location}</span>
          <span className="text-muted-foreground"> · </span>
          <span>{designLabel(setup)}</span>
          <span className="text-muted-foreground"> · </span>
          <span>{BAND_LABEL[setup.sizeBand]}</span>
        </dd>
        <dt className="text-muted-foreground">Art</dt>
        <dd>{printInchesLabel(setup.design, setup.sizeBand) ?? 'size unknown — file not ready'}</dd>
        <dt className="text-muted-foreground">Position</dt>
        <dd>{placementCaption(placed)}</dd>
        <dt className="text-muted-foreground">Alignment</dt>
        <dd>
          {aligned ? (
            <Badge
              variant="outline"
              className="border-emerald-300 bg-emerald-50 font-normal text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300"
            >
              box set
            </Badge>
          ) : (
            <span className="text-muted-foreground">
              whole file — the guides centre the file box
            </span>
          )}
        </dd>
      </dl>
    </div>
  );
}

// ── Screens ───────────────────────────────────────────────────────────────────────────────────────

/**
 * The band-correct master, served as an attachment the server names (`<styleCode ?? name>-<band>-
 * <W>x<H>in.png`). One per SETUP, not per colour: for multi-colour art the per-colour positive is the
 * RIP's separation (P4b), and three identical "master" buttons would say otherwise.
 */
function DownloadOriginal({ setup }: { setup: SetupWithSpec }) {
  const file = setup.sizeBand === 'youth' ? setup.design?.youth : setup.design?.adult;
  const hint = [
    setup.design?.styleCode ?? setup.design?.name,
    setup.sizeBand,
    printInchesLabel(setup.design, setup.sizeBand),
  ]
    .filter(Boolean)
    .join(' · ');
  if (file?.downloadUrl) {
    return (
      <Button asChild size="sm" variant="outline">
        <a href={file.downloadUrl} title={hint}>
          <Download className="mr-1.5 size-4" />
          Download original
          <span className="ml-1.5 hidden font-normal text-muted-foreground sm:inline">{hint}</span>
        </a>
      </Button>
    );
  }
  const reason = !setup.designId
    ? 'The line’s design never resolved, so there is no file to download.'
    : !setup.design
      ? 'Design not found in the library.'
      : file?.status !== 'READY'
        ? `The ${setup.sizeBand} file is not READY yet.`
        : 'The server did not return a download link — the API predates it.';
  return (
    <Button size="sm" variant="outline" disabled title={reason}>
      <Download className="mr-1.5 size-4" />
      Download original
    </Button>
  );
}

function ScreenCard({
  screen,
  colours,
  automatic,
}: {
  screen: ScreenSpec;
  colours: number;
  automatic: boolean;
}) {
  return (
    <div className="grid grid-cols-[32px_1fr] gap-3 border-t px-3 py-2" data-testid="screen-card">
      <div
        className="flex size-8 items-center justify-center rounded-full bg-muted font-mono text-sm font-bold tabular-nums"
        title={screen.head != null ? `Head ${screen.head}` : 'No head assigned yet'}
      >
        {screen.head ?? '—'}
      </div>
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-1.5 text-sm">
          <span className="font-medium">{screen.name}</span>
          {screen.flashAfter && (
            <Badge variant="outline" className="font-normal">
              flash after
            </Badge>
          )}
          {screen.head == null && automatic && (
            <Badge
              variant="outline"
              className="border-amber-300 bg-amber-50 font-normal text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300"
            >
              no head yet
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Chip title="Pantone">
            <Swatch hex={screen.hex} />
            {screen.pantone ? (
              <b>{screen.pantone}</b>
            ) : (
              <span className="text-muted-foreground">no Pantone</span>
            )}
            {screen.hex && !screen.pantone && <span className="font-mono">{screen.hex}</span>}
          </Chip>
          <Chip title="Ink and mix">
            ink <b>{screen.ink ?? '—'}</b>
          </Chip>
          <Chip title="Mesh count">
            mesh <b>{screen.meshCount ?? '—'}</b>
          </Chip>
          <Chip title="Frame">
            frame <b>{screen.frame ?? '—'}</b>
          </Chip>
        </div>
        <p className="text-xs text-muted-foreground">
          {colours === 1
            ? '1 colour — the master is the positive.'
            : 'Positive: this colour’s separation from the RIP.'}
        </p>
      </div>
    </div>
  );
}

function SetupGroup({ setup, automatic }: { setup: SetupWithSpec; automatic: boolean }) {
  const n = setup.screens.length;
  return (
    <div className="overflow-hidden rounded-lg border" data-testid="setup-group">
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 bg-muted/40 px-3 py-2 text-sm">
        <span className="font-medium capitalize">{setup.location}</span>
        <span className="text-muted-foreground">·</span>
        <span>{designLabel(setup)}</span>
        {setup.design?.styleCode && (
          <Badge variant="outline" className="font-mono font-normal">
            {setup.design.styleCode}
          </Badge>
        )}
        <span className="text-muted-foreground">·</span>
        <span>{BAND_LABEL[setup.sizeBand]}</span>
        <span className="ml-auto text-xs text-muted-foreground">
          {setup.garments} garments ·{' '}
          {setup.spec ? `${n} colour${n === 1 ? '' : 's'}` : 'no screen spec'}
        </span>
      </div>
      {setup.screens.map((c) => (
        <ScreenCard key={c.seq} screen={c} colours={n} automatic={automatic} />
      ))}
      {!setup.spec && (
        <p className="border-t px-3 py-2 text-sm text-muted-foreground">
          {setup.designId ? (
            <>
              No screen spec on this design yet — colours, mesh, frame, ink and Pantone come from{' '}
              <Link to="/designs" className="underline">
                Designs → Edit → Screen print
              </Link>
              .
            </>
          ) : (
            'The line’s design never resolved, so there is nothing to look up.'
          )}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2 border-t px-3 py-2">
        <DownloadOriginal setup={setup} />
      </div>
    </div>
  );
}

// ── The packet ────────────────────────────────────────────────────────────────────────────────────

export function RunPacket({
  run,
  nameBySku,
  leading,
  trailing,
}: {
  run: BatchSummary;
  nameBySku: ReadonlyMap<string, BlankOnHand>;
  /** Surface-specific buttons drawn before the Job ticket (the office: Pick list · Print labels). */
  leading?: ReactNode;
  /** … and after it (the office: Press station · Mark printed · Delete run). */
  trailing?: ReactNode;
}) {
  const client = useApiClient();
  const work = run.screenPrintWork ?? [];
  const setups = useMemo(() => runSetups(work), [work]);
  const designs = useDesignsById();
  const joined = useMemo(() => joinSpecs(setups, designs.byId), [setups, designs.byId]);
  const presses = usePresses();
  const press = resolveRunPress(presses.data ?? [], joined);
  const ring = ringScreensForRun(joined);
  const layout = stationLayout(ring, press);
  const specsKnown = !designs.isLoading && !designs.isError;
  const screenCount = specsKnown ? runScreenCount(joined) : undefined;
  const missing = specsKnown ? setupsMissingSpec(joined) : [];
  const pulls = blanksToPull({ transfers: [] }, nameBySku, work);
  const pullTotal = pulls.reduce((s, p) => s + p.qty, 0);
  const garments = runGarmentCount(work);
  const products = distinctProducts(work);
  const productImages = useProductImages(products);
  const blankSku = firstBlankSku(work);
  const blank = blankSku ? nameBySku.get(blankSku) : undefined;
  const cure = cureLines(joined);
  const platen = platenById(DEFAULT_PLATEN_ID);
  const [ticketOpen, setTicketOpen] = useState(false);
  const meta = BATCH_STATUS_META[run.status];
  const brand = run.brandLabel ?? run.storeIds.join(', ');
  const hasYouth = joined.some((s) => s.sizeBand === 'youth');

  return (
    <div className="mt-4 space-y-4 rounded-lg border p-4" data-testid="run-packet">
      {/* header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h2 className="flex flex-wrap items-center gap-2 text-lg font-semibold">
            {batchLabel(run)}
            <span className="font-normal text-muted-foreground">· {brand}</span>
            <Badge variant="outline" className={meta.className}>
              {meta.label}
            </Badge>
            <span
              className="text-xs font-normal text-muted-foreground"
              title={new Date(run.createdAt).toLocaleString()}
            >
              built {relativeAge(run.createdAt, Date.now())}
            </span>
          </h2>
          <p className="flex flex-wrap gap-x-3 text-sm" data-testid="run-counts">
            <span>
              <b className="tabular-nums">{garments}</b> garments
            </span>
            <span>
              <b className="tabular-nums">{setups.length}</b> setup{setups.length === 1 ? '' : 's'}
            </span>
            {designs.isLoading ? (
              <span className="text-muted-foreground">screens …</span>
            ) : designs.isError ? (
              <span className="text-muted-foreground">designs unavailable</span>
            ) : screenCount != null ? (
              <span>
                <b className="tabular-nums">{screenCount}</b> screen{screenCount === 1 ? '' : 's'}
              </span>
            ) : (
              <span className="text-amber-700 dark:text-amber-400">colours not set</span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {leading}
          <Button size="sm" variant="outline" onClick={() => setTicketOpen(true)}>
            <FileText className="mr-1.5 size-4" />
            Job ticket
          </Button>
          {trailing}
        </div>
      </div>

      {/* missing-spec strip — warn, never refuse (§ 7.3): the run builds, presses and settles as today */}
      {missing.length > 0 && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200"
        >
          <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <div className="space-y-0.5">
            {missing.map((s) => (
              <p key={s.key}>
                {s.designId ? (
                  <>
                    <b>{designLabel(s)}</b> has no screen spec yet, so its screens are not counted.{' '}
                    <Link to="/designs" className="underline">
                      Set it in Designs → Edit → Screen print
                    </Link>
                    .
                  </>
                ) : (
                  <>
                    <b>design unknown</b> at <span className="capitalize">{s.location}</span> — the
                    line’s design never resolved, so its screens are not counted.
                  </>
                )}
              </p>
            ))}
          </div>
        </div>
      )}

      {ticketOpen && (
        <JobTicket
          label={batchLabel(run)}
          brand={brand}
          garments={garments}
          setups={joined}
          press={press}
          pulls={pulls}
          onClose={() => setTicketOpen(false)}
          onPrint={() => void stampPull(client, run.batchId, 'pull-list')}
        />
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* LEFT */}
        <div className="space-y-4">
          <Section title="Mockup" hint={products.length === 1 ? products[0]?.title : undefined}>
            <div className="rounded-lg border p-3">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {products.map((p) => (
                  <ProductPhoto
                    key={productKey(p.storeId, p.productId)}
                    title={p.title}
                    url={productImages.images[productKey(p.storeId, p.productId)]}
                    loading={productImages.isLoading}
                  />
                ))}
                {joined.map((s) => (
                  <ArtOnBlank key={s.key} setup={s} blank={blank} />
                ))}
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">
                {products.length === 0
                  ? 'Shopify image unavailable — this run was built before the product was recorded on it. '
                  : ''}
                Placement approximate — the guides are on the press.
              </p>
            </div>
          </Section>

          <Section title={`Press · ${press.name}`} hint={pressShapeLabel(press)}>
            <div className="rounded-lg border p-3">
              <PressCarousel
                press={press}
                screens={ring}
                caption={[press.name, 'head order = print order']}
                className="mx-auto w-full max-w-[260px]"
              />
              {layout.warnings.length > 0 && (
                <ul className="mt-2 space-y-1" aria-label="Press layout warnings">
                  {layout.warnings.map((w) => (
                    <li
                      key={w}
                      className="flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300"
                    >
                      <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                      {w}
                    </li>
                  ))}
                </ul>
              )}
              {ring.length === 0 && (
                <p className="mt-2 text-center text-xs text-muted-foreground">
                  No colours to place yet — the ring fills from each design’s screen spec.
                </p>
              )}
            </div>
          </Section>

          <Section title="Ink & cure">
            <div className="rounded-lg border p-3">
              {cure.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No cure or press notes on this run’s designs.
                </p>
              ) : (
                <dl className="grid grid-cols-[84px_1fr] gap-x-2 gap-y-1 text-sm">
                  {cure.map((c) => (
                    <div key={c.design} className="contents">
                      <dt className="text-muted-foreground">{c.design}</dt>
                      <dd>
                        <b>{c.cure ?? 'cure not set'}</b>
                        {c.notes && <div className="text-muted-foreground">{c.notes}</div>}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          </Section>

          <Section
            title="Pallet & placement"
            hint={`pallet ${platen.acrossIn} × ${platen.deepIn} · default`}
          >
            <div className="space-y-3 rounded-lg border p-3">
              {joined.map((s) => (
                <PalletRow key={s.key} setup={s} platen={platen} />
              ))}
              <p className="text-[11px] text-muted-foreground">
                {hasYouth ? 'Youth lines on this run.' : 'No youth lines on this run.'} The dashed
                green box is what the guides line up with; the pallet is the shop default until the
                run records its own.
              </p>
            </div>
          </Section>
        </div>

        {/* RIGHT */}
        <div className="space-y-4">
          <Section
            title={
              <>
                <Layers className="size-3.5" aria-hidden="true" /> Screens to burn
              </>
            }
            hint="one card per colour · print order top → bottom"
          >
            <div className="space-y-3">
              {joined.map((s) => (
                <SetupGroup key={s.key} setup={s} automatic={press.kind === 'automatic'} />
              ))}
            </div>
          </Section>

          <Section title={`Blanks to pull · ${pullTotal}`}>
            <ul className="space-y-1 rounded-lg border p-3 text-sm">
              {pulls.map((p) => (
                <li key={p.key} className="flex items-baseline justify-between gap-3">
                  <span title={p.titles.join(', ')}>
                    {p.label}
                    {p.variant && <span className="text-muted-foreground"> · {p.variant}</span>}
                  </span>
                  <span className="tabular-nums font-medium">{p.qty}</span>
                </li>
              ))}
              {pulls.length === 0 && <li className="text-muted-foreground">Nothing to pull.</li>}
            </ul>
          </Section>

          <Section
            title="Press progress"
            hint={
              <Link to="/press" className="underline">
                Open press station →
              </Link>
            }
          >
            {/* No tick and no "pressed" button here — pressing stays on /press (plan § 7). */}
            <ul className="space-y-2 rounded-lg border p-3 text-sm">
              {joined.map((s) => {
                const pct = s.garments > 0 ? Math.round((100 * s.pressed) / s.garments) : 0;
                return (
                  <li key={s.key} className="space-y-1">
                    <div className="flex items-baseline justify-between gap-3">
                      <span>
                        <span className="font-medium capitalize">{s.location}</span>
                        <span className="text-muted-foreground">
                          {' · '}
                          {designLabel(s)}
                          {' · '}
                          {BAND_LABEL[s.sizeBand]}
                        </span>
                      </span>
                      <span className="tabular-nums text-muted-foreground">
                        {s.pressed}/{s.garments} pressed
                      </span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded bg-muted" aria-hidden="true">
                      <div className="h-full rounded bg-emerald-500" style={{ width: `${pct}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          </Section>
        </div>
      </div>
    </div>
  );
}
